import { useCallback, useEffect, useMemo, useState } from 'react'
import { ApiError, can, refusalReason, shortDate, tierLabel } from '../lib/api'
import {
  BATCH_STEPS,
  EMPTY_PICK,
  TIER_ORDINALS,
  baselines,
  batchBody,
  movedVendors,
  needsSendReason,
  offered,
  pickRows,
  proposalSummary,
  reviewOf,
  rowState,
  stepOpen,
  type BatchApproval,
  type BatchApproved,
  type BatchCreated,
  type BatchStep,
  type CampaignBatch as Batch,
  type ProposalRow,
  type ProposeAnswer,
  type RowChoice,
  type VendorGroup,
  type VendorPick,
} from '../lib/baselines'
import { checkDueAfterToday, todayIso } from '../lib/dateRules'
import { ErrorBox, Loading } from '../components/DataTable'
import { Status } from '../components/kit'
import { ReasonTip } from '../components/vendor/ReasonTip'
import '../styles/baselines.css'

// A bulk send that proposes a baseline per vendor (release 3.4, M7), at
// #/campaigns/new-batch, and one already raised at #/campaigns/new-batch?id=.
//
// # WHY THIS SCREEN EXISTS
//
// A campaign sends one questionnaire to everybody in it, so a programme with a
// payment assessment, a cloud one and the tier defaults raised three campaigns
// by hand and kept the lists in step itself. Here the person chooses vendors
// once (step 1), the server proposes each one's baseline and says why (step 2,
// the rules under Settings, Baselines), a row may be changed with a reason or
// put back, and the review (step 3) says how many vendors each baseline goes
// to and who approves. The server stores it as one draft campaign per baseline
// under one bulk send, through the campaign path, so the request gate, the
// answer-by rule and the second person are the ones every send has. Nothing is
// sent before a second person approves.

function asApiError(err: unknown): ApiError {
  return err instanceof ApiError ? err : new ApiError(0, 'UNKNOWN', String(err))
}

function batchIdFromHash(): string {
  return new URLSearchParams(window.location.hash.split('?')[1] ?? '').get('id') ?? ''
}

const STATUS_CHOICES: [string, string][] = [
  ['ACTIVE', 'Active'],
  ['DRAFT', 'In due diligence'],
  ['SUSPENDED', 'Suspended'],
]

export function CampaignBatch() {
  const [batchId, setBatchId] = useState(batchIdFromHash)
  useEffect(() => {
    const on = () => setBatchId(batchIdFromHash())
    window.addEventListener('hashchange', on)
    return () => window.removeEventListener('hashchange', on)
  }, [])
  if (batchId) return <BatchView id={batchId} />
  // Assembling a send is assessment.plan, as a campaign is; somebody who only
  // reads campaigns is told so rather than shown a refusal from the proposal.
  if (!can('assessment.plan')) {
    return (
      <>
        <Head title="Bulk send with baselines" />
        <p className="sw-row">
          <Status tone="neutral">Read only</Status>
          <ReasonTip label="Why you cannot assemble a bulk send" text={refusalReason('assessment.plan')} />
        </p>
      </>
    )
  }
  return <NewBatch />
}

function Head({ title }: { title: string }) {
  return (
    <div className="p1-head">
      <div>
        <p className="bl-crumb">
          <a href="#/campaigns">Bulk assessments</a>
        </p>
        <h1>{title}</h1>
      </div>
    </div>
  )
}

export function Steps({ step, open, onGo }: { step: BatchStep; open: (s: BatchStep) => boolean; onGo: (s: BatchStep) => void }) {
  return (
    <ol className="bl-steps" aria-label="Steps">
      {BATCH_STEPS.map((s, i) => (
        <li key={s.key}>
          <button type="button" aria-current={step === s.key ? 'step' : undefined} disabled={!open(s.key)} onClick={() => onGo(s.key)}>
            <span className="bl-step-no">{i + 1}</span> {s.label}
          </button>
        </li>
      ))}
    </ol>
  )
}

function NewBatch() {
  const [answer, setAnswer] = useState<ProposeAnswer | null>(null)
  const [groups, setGroups] = useState<VendorGroup[]>([])
  const [error, setError] = useState<unknown>(null)
  const [step, setStep] = useState<BatchStep>('vendors')
  const [name, setName] = useState('')
  const [reason, setReason] = useState('')
  const [pick, setPick] = useState<VendorPick>(EMPTY_PICK)
  const [chosen, setChosen] = useState<string[]>([])
  const [choices, setChoices] = useState<Record<string, RowChoice>>({})
  const [oneDate, setOneDate] = useState(false)
  const [dueOn, setDueOn] = useState('')
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState<ApiError | null>(null)
  const [created, setCreated] = useState<BatchCreated | null>(null)

  const readProposal = useCallback(() => {
    baselines
      .proposeAll()
      .then((a) => {
        setAnswer(a)
        setError(null)
      })
      .catch(setError)
  }, [])

  useEffect(() => {
    readProposal()
    baselines
      .groups()
      .then((g) => setGroups(g.groups))
      .catch(() => setGroups([]))
  }, [readProposal])

  const offer = useMemo(() => (answer?.proposals ?? []).filter(offered), [answer])
  const notOffered = (answer?.proposals.length ?? 0) - offer.length
  const shown = useMemo(() => pickRows(offer, pick), [offer, pick])
  const chosenRows = useMemo(() => offer.filter((r) => chosen.includes(r.vendorId)), [offer, chosen])
  const questionnaires = answer?.questionnaires ?? []
  const review = useMemo(
    () => (chosenRows.length ? reviewOf(chosenRows, choices, questionnaires, oneDate ? dueOn : '') : null),
    [chosenRows, choices, questionnaires, oneDate, dueOn],
  )
  const askReason = needsSendReason(chosenRows)
  const dateRule = oneDate && dueOn ? checkDueAfterToday('Answer-by date', dueOn, todayIso()) : ''
  const open = (s: BatchStep) => stepOpen(s, chosenRows.length, review, name) && !(s === 'review' && askReason && !reason.trim())

  function create() {
    setCreating(true)
    setCreateError(null)
    baselines
      .createBatch(batchBody(name, oneDate ? dueOn : '', reason, chosenRows, choices, answer?.rulesVersion ?? 0))
      .then(setCreated)
      .catch((err) => setCreateError(asApiError(err)))
      .finally(() => setCreating(false))
  }

  if (error) return <ErrorBox error={error} what="The vendors and their baselines" />
  if (!answer) return <Loading what="the vendors and their baselines" />

  if (created) {
    return (
      <>
        <Head title="Bulk send with baselines" />
        <section className="panel">
          <div className="note stop" role="status">
            <strong>Created. Nothing has been sent.</strong>
            {created.campaigns.length} campaign{created.campaigns.length === 1 ? '' : 's'} wait for a second person to approve them.
          </div>
          <ul className="bl-created">
            {created.campaigns.map((c) => (
              <li key={c.id}>
                <a href={'#/campaign/' + encodeURIComponent(c.id)}>{c.name}</a>{' '}
                <span className="muted">
                  {c.recipients} vendor{c.recipients === 1 ? '' : 's'}, answer by {shortDate(c.dueOn)}
                </span>
              </li>
            ))}
          </ul>
          {created.skipped.length > 0 && (
            <p className="muted">
              {created.skipped.length} left out: {created.skipped.map((s) => s.reason).filter((v, i, a) => a.indexOf(v) === i).join('; ')}
            </p>
          )}
          <a className="button primary" href={'#/campaigns/new-batch?id=' + encodeURIComponent(created.id)}>
            Open the bulk send
          </a>
        </section>
      </>
    )
  }

  const nameOf = (id: string) => questionnaires.find((q) => q.id === id)?.name ?? ''

  return (
    <>
      <Head title="Bulk send with baselines" />
      <Steps step={step} open={open} onGo={setStep} />

      {step === 'vendors' && (
        <section className="panel bl-step" aria-label="Vendors">
          <div className="bl-step-head">
            <div className="field bl-name">
              <label htmlFor="bl-send-name">Name of this send</label>
              <input id="bl-send-name" value={name} maxLength={160} placeholder="Q4 reassessment" onChange={(e) => setName(e.target.value)} />
            </div>
            <p className="summary-line" role="status">
              <strong>{chosenRows.length}</strong> chosen of {offer.length} offered
              {notOffered > 0 && (
                <>
                  {' '}
                  <ReasonTip
                    label="Why some vendors are not offered"
                    text={
                      notOffered +
                      ' vendor' +
                      (notOffered === 1 ? ' is' : 's are') +
                      ' not offered: a request not approved yet, awaiting approval, or nobody with an address to invite.'
                    }
                  />
                </>
              )}
            </p>
          </div>
          {answer.note && (
            <p className="note warn" role="status">
              {answer.note}
            </p>
          )}
          <Filters pick={pick} setPick={setPick} rows={offer} groups={groups} />
          <VendorTable rows={shown} chosen={chosen} setChosen={setChosen} />
          <div className="bl-foot-bar">
            <a href="#/campaigns">Cancel</a>
            <button type="button" className="primary" disabled={!open('baselines')} onClick={() => setStep('baselines')}>
              Next: baselines
            </button>
          </div>
        </section>
      )}

      {step === 'baselines' && review && (
        <section className="panel bl-step" aria-label="Baselines">
          <p className="summary-line" role="status">
            {proposalSummary(review)}
          </p>
          {askReason && (
            <div className="field bl-reason">
              <label htmlFor="bl-send-reason">Reason</label>
              <textarea id="bl-send-reason" rows={2} maxLength={1000} value={reason} aria-describedby="bl-send-reason-note" onChange={(e) => setReason(e.target.value)} />
              <p className="field-note" id="bl-send-reason-note">
                A suspended vendor is in this send. Say why it is sent due diligence.
              </p>
            </div>
          )}
          <ProposalTable rows={chosenRows} choices={choices} setChoices={setChoices} questionnaires={questionnaires} nameOf={nameOf} />
          <div className="bl-foot-bar">
            <button type="button" onClick={() => setStep('vendors')}>
              Back
            </button>
            {!name.trim() && <span className="field-note">Name this send on the first step.</span>}
            <button type="button" className="primary" disabled={!open('review')} onClick={() => setStep('review')}>
              Next: review
            </button>
          </div>
        </section>
      )}

      {step === 'review' && review && (
        <section className="panel bl-step" aria-label="Review">
          <h2>{name}</h2>
          <fieldset className="bl-due">
            <legend>Answer by</legend>
            <label className="radio-inline">
              <input type="radio" name="bl-due" checked={!oneDate} onChange={() => setOneDate(false)} />
              Each baseline's own days, from today
            </label>
            <label className="radio-inline">
              <input type="radio" name="bl-due" checked={oneDate} onChange={() => setOneDate(true)} />
              One date for every vendor
            </label>
            {oneDate && (
              <div className="field">
                <label htmlFor="bl-due-on" className="vh">
                  Answer-by date
                </label>
                <input id="bl-due-on" type="date" value={dueOn} min={todayIso()} aria-invalid={dateRule ? true : undefined} onChange={(e) => setDueOn(e.target.value)} />
                {dateRule && <p className="field-error">{dateRule}</p>}
              </div>
            )}
          </fieldset>
          <div className="table-wrap">
            <table className="grid">
              <caption className="vh">How many vendors each baseline goes to.</caption>
              <thead>
                <tr>
                  <th scope="col">Baseline</th>
                  <th scope="col" className="num">
                    Vendors
                  </th>
                  <th scope="col">Answer by</th>
                </tr>
              </thead>
              <tbody>
                {review.baselines.map((b) => (
                  <tr key={b.questionnaireId + b.dueIn}>
                    <td>{b.name}</td>
                    <td className="num mono">{b.vendors}</td>
                    <td>{oneDate && dueOn ? shortDate(dueOn) : b.dueIn}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {review.changed.length > 0 && (
            <>
              <h3>Changed by you</h3>
              <ul className="bl-changes">
                {review.changed.map((c) => (
                  <li key={c.vendor}>
                    <strong>{c.vendor}</strong>: {c.from} to {c.to}. <span className="muted">{c.reason}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
          <div className="note stop">
            <strong>Who approves</strong>A person other than you who may issue a campaign to vendors. Nothing is sent before then.
          </div>
          {createError && createError.code === 'BASELINES_CHANGED' ? (
            <div className="note warn" role="alert">
              <strong>The baselines changed since you read them. Nothing was drafted.</strong>
              {createError.message}
              {movedVendors(createError.body).length > 0 && (
                <ul className="bl-changes">
                  {movedVendors(createError.body).map((m) => (
                    <li key={m.vendorId}>
                      <strong>{m.name}</strong>: shown {m.shown}, now {m.now}. <span className="muted">{m.why}</span>
                    </li>
                  ))}
                </ul>
              )}
              <button
                type="button"
                onClick={() => {
                  setCreateError(null)
                  readProposal()
                  setStep('baselines')
                }}
              >
                Read the proposal again
              </button>
            </div>
          ) : (
            createError && <ErrorBox error={createError} what="The bulk send" verb="created" />
          )}
          <div className="bl-foot-bar">
            <button type="button" onClick={() => setStep('baselines')}>
              Back
            </button>
            {can('assessment.plan') ? (
              <button
                type="button"
                className="primary"
               
                disabled={creating || Boolean(dateRule) || (oneDate && !dueOn)}
                onClick={create}
              >
                {creating ? 'Creating' : 'Create ' + review.baselines.length + ' campaign' + (review.baselines.length === 1 ? '' : 's')}
              </button>
            ) : (
              <span className="sw-row">
                <Status tone="neutral">Read only</Status>
                <ReasonTip label="Why you cannot create a bulk send" text={refusalReason('assessment.plan')} />
              </span>
            )}
          </div>
        </section>
      )}
    </>
  )
}

function Filters({ pick, setPick, rows, groups }: { pick: VendorPick; setPick: (p: VendorPick) => void; rows: ProposalRow[]; groups: VendorGroup[] }) {
  const toggle = <K extends 'groups' | 'tiers' | 'types' | 'statuses'>(key: K, value: VendorPick[K][number]) => {
    const list = pick[key] as (string | number)[]
    setPick({ ...pick, [key]: list.includes(value) ? list.filter((x) => x !== value) : [...list, value] })
  }
  const types = [...new Map(rows.filter((r) => r.vendorType).map((r) => [r.vendorType!, r.vendorTypeLabel ?? r.vendorType!])).entries()].sort((a, b) =>
    a[1].localeCompare(b[1]),
  )
  const owners = [...new Set(rows.map((r) => r.owner).filter(Boolean) as string[])].sort()
  return (
    <div className="bl-filters">
      {groups.length > 0 && (
        <div className="filter-group">
          <span className="label">Group</span>
          <div className="chiprow">
            {groups.map((g) => (
              <button key={g.id} type="button" className="chip-toggle" aria-pressed={pick.groups.includes(g.id)} onClick={() => toggle('groups', g.id)}>
                {g.name}
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="filter-group">
        <span className="label">Tier</span>
        <div className="chiprow">
          {TIER_ORDINALS.map((t) => (
            <button key={t} type="button" className="chip-toggle" aria-pressed={pick.tiers.includes(t)} onClick={() => toggle('tiers', t)}>
              {tierLabel(t)}
            </button>
          ))}
        </div>
      </div>
      <div className="filter-group">
        <span className="label">Status</span>
        <div className="chiprow">
          {STATUS_CHOICES.map(([code, label]) => (
            <button key={code} type="button" className="chip-toggle" aria-pressed={pick.statuses.includes(code)} onClick={() => toggle('statuses', code)}>
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="bl-filter-fields">
        <div className="field">
          <label htmlFor="bl-type">Type</label>
          <select id="bl-type" value={pick.types[0] ?? ''} onChange={(e) => setPick({ ...pick, types: e.target.value ? [e.target.value] : [] })}>
            <option value="">Any type</option>
            {types.map(([code, label]) => (
              <option key={code} value={code}>
                {label}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="bl-owner">Owner</label>
          <select id="bl-owner" value={pick.owner} onChange={(e) => setPick({ ...pick, owner: e.target.value })}>
            <option value="">Anybody</option>
            {owners.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="bl-q">Name</label>
          <input id="bl-q" type="search" value={pick.q} placeholder="Name or vendor code" onChange={(e) => setPick({ ...pick, q: e.target.value })} />
        </div>
      </div>
    </div>
  )
}

export function VendorTable({ rows, chosen, setChosen }: { rows: ProposalRow[]; chosen: string[]; setChosen: (ids: string[]) => void }) {
  const ids = rows.map((r) => r.vendorId)
  const allIn = ids.length > 0 && ids.every((id) => chosen.includes(id))
  return (
    <div className="table-wrap bl-scroll">
      <table className="grid bl-vendors">
        <caption className="vh">Vendors the filters show. Choose the ones this send goes to.</caption>
        <thead>
          <tr>
            <th scope="col">
              <label className="bl-check">
                <input
                  type="checkbox"
                  checked={allIn}
                  disabled={ids.length === 0}
                  onChange={() => setChosen(allIn ? chosen.filter((x) => !ids.includes(x)) : [...new Set([...chosen, ...ids])])}
                />
                <span className="vh">Choose all {ids.length} shown</span>
              </label>
            </th>
            <th scope="col">Vendor</th>
            <th scope="col">Tier</th>
            <th scope="col">Type</th>
            <th scope="col">Groups</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.vendorId}>
              <td>
                <input
                  type="checkbox"
                  aria-label={'Choose ' + r.name}
                  checked={chosen.includes(r.vendorId)}
                  onChange={() => setChosen(chosen.includes(r.vendorId) ? chosen.filter((x) => x !== r.vendorId) : [...chosen, r.vendorId])}
                />
              </td>
              <td>
                {r.name}
                <span className="cell-sub mono">{r.vendorCode}</span>
              </td>
              <td>{r.tierLabel ?? <span className="muted">no tier</span>}</td>
              <td>{r.vendorTypeLabel ?? <span className="muted">not set</span>}</td>
              <td>{r.groups.length ? r.groups.join(', ') : <span className="muted">none</span>}</td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={5} className="muted">
                No vendor matches these filters.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

export function ProposalTable({
  rows,
  choices,
  setChoices,
  questionnaires,
  nameOf,
}: {
  rows: ProposalRow[]
  choices: Record<string, RowChoice>
  setChoices: (c: Record<string, RowChoice>) => void
  questionnaires: { id: string; name: string }[]
  nameOf: (id: string) => string
}) {
  const [checked, setChecked] = useState<string[]>([])
  const [bulkTo, setBulkTo] = useState('')
  const [bulkReason, setBulkReason] = useState('')
  const set = (id: string, patch: RowChoice) => setChoices({ ...choices, [id]: { ...choices[id], ...patch } })
  const reset = (id: string) => {
    const next = { ...choices }
    delete next[id]
    setChoices(next)
  }
  const applyBulk = useCallback(() => {
    const next = { ...choices }
    for (const id of checked) next[id] = { questionnaireId: bulkTo, reason: bulkReason }
    setChoices(next)
    setChecked([])
  }, [checked, choices, bulkTo, bulkReason, setChoices])

  return (
    <>
      {checked.length > 0 && (
        <div className="bl-bulk" role="group" aria-label="Change the rows chosen">
          <span>
            <strong>{checked.length}</strong> chosen
          </span>
          <label htmlFor="bl-bulk-to" className="vh">
            Send them
          </label>
          <select id="bl-bulk-to" value={bulkTo} onChange={(e) => setBulkTo(e.target.value)}>
            <option value="">Send them</option>
            {questionnaires.map((q) => (
              <option key={q.id} value={q.id}>
                {q.name}
              </option>
            ))}
          </select>
          <label htmlFor="bl-bulk-reason" className="vh">
            Reason
          </label>
          <input id="bl-bulk-reason" value={bulkReason} placeholder="Reason" maxLength={1000} onChange={(e) => setBulkReason(e.target.value)} />
          <button type="button" disabled={!bulkTo} onClick={applyBulk}>
            Change
          </button>
        </div>
      )}
      <div className="table-wrap bl-scroll">
        <table className="grid bl-proposals">
          <caption className="vh">Each vendor, the baseline proposed for it and why, and the one it is sent.</caption>
          <thead>
            <tr>
              <th scope="col">
                <span className="vh">Choose</span>
              </th>
              <th scope="col">Vendor</th>
              <th scope="col">Tier</th>
              <th scope="col">Proposed baseline (why)</th>
              <th scope="col">Sent</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const s = rowState(r, choices[r.vendorId], nameOf)
              const value = choices[r.vendorId]?.questionnaireId ?? ''
              const changed = s.kind === 'changed' || s.kind === 'needs-reason'
              return (
                <tr key={r.vendorId} className={changed ? 'bl-changed' : s.kind === 'needs-choice' ? 'bl-waiting' : undefined}>
                  <td>
                    <input
                      type="checkbox"
                      aria-label={'Choose ' + r.name}
                      checked={checked.includes(r.vendorId)}
                      onChange={() => setChecked(checked.includes(r.vendorId) ? checked.filter((x) => x !== r.vendorId) : [...checked, r.vendorId])}
                    />
                  </td>
                  <td>
                    {r.name}
                    <span className="cell-sub">{r.vendorTypeLabel ?? ''}</span>
                  </td>
                  <td>{r.tierLabel ?? <span className="muted">no tier</span>}</td>
                  <td>
                    {r.proposal.source === 'NONE' ? <span className="muted">None</span> : r.proposal.questionnaire.name}
                    <span className="cell-sub">{r.why}</span>
                  </td>
                  <td className="bl-sent">
                    <label htmlFor={'bl-sent-' + r.vendorId} className="vh">
                      Questionnaire sent to {r.name}
                    </label>
                    <select
                      id={'bl-sent-' + r.vendorId}
                      value={value}
                      aria-invalid={s.kind === 'needs-choice' ? true : undefined}
                      onChange={(e) => set(r.vendorId, { questionnaireId: e.target.value })}
                    >
                      <option value="">{r.proposal.source === 'NONE' ? 'Choose one' : 'As proposed'}</option>
                      {questionnaires.map((q) => (
                        <option key={q.id} value={q.id}>
                          {q.name}
                        </option>
                      ))}
                    </select>
                    {changed && (
                      <>
                        <label htmlFor={'bl-why-' + r.vendorId} className="vh">
                          Reason for the change
                        </label>
                        <input
                          id={'bl-why-' + r.vendorId}
                          value={choices[r.vendorId]?.reason ?? ''}
                          placeholder="Reason, kept on the record"
                          maxLength={1000}
                          aria-invalid={s.kind === 'needs-reason' ? true : undefined}
                          onChange={(e) => set(r.vendorId, { reason: e.target.value })}
                        />
                      </>
                    )}
                    {s.kind === 'needs-choice' && <span className="field-error">Choose one.</span>}
                    {changed && <span className="cell-sub">{s.kind === 'needs-reason' ? 'Changed, give a reason' : 'Changed by you'}</span>}
                    {choices[r.vendorId] && (
                      <button type="button" className="linkish" onClick={() => reset(r.vendorId)}>
                        Reset
                      </button>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </>
  )
}

// ------------------------------------------------------------ one raised

function BatchView({ id }: { id: string }) {
  const [batch, setBatch] = useState<Batch | null>(null)
  const [approval, setApproval] = useState<BatchApproval | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [approving, setApproving] = useState(false)
  const [approveError, setApproveError] = useState<ApiError | null>(null)
  const [approved, setApproved] = useState<BatchApproved | null>(null)

  const load = useCallback(() => {
    baselines
      .batch(id)
      .then((r) => {
        setBatch(r.batch)
        setApproval(r.approval)
        setError(null)
      })
      .catch(setError)
  }, [id])
  useEffect(load, [load])

  function approve() {
    setApproving(true)
    setApproveError(null)
    baselines
      .approveBatch(id)
      .then((r) => {
        setApproved(r)
        load()
      })
      .catch((err) => {
        setApproveError(asApiError(err))
        // A refusal part way (NOT_ENTITLED) may still have approved some
        // campaigns: read the send again so their rows do not say Waiting.
        load()
      })
      .finally(() => setApproving(false))
  }

  if (error) return <ErrorBox error={error} what="The bulk send" />
  if (!batch || !approval) return <Loading what="the bulk send" />
  const waiting = batch.campaigns.filter((c) => c.status === 'DRAFT' || c.status === 'PENDING_APPROVAL').length

  return (
    <>
      <Head title={batch.name} />
      <section className="panel">
        <p className="sw-row">
          <Status tone={batch.status === 'DRAFT' ? 'warn' : 'plain'}>{batch.status === 'DRAFT' ? 'Waiting for approval' : batch.status === 'APPROVED' ? 'Approved' : 'Cancelled'}</Status>
          <span className="muted">
            Raised by <span className="mono">{batch.createdBy}</span>
            {batch.approvedBy ? (
              <>
                , approved by <span className="mono">{batch.approvedBy}</span>
              </>
            ) : null}
          </span>
        </p>
        {batch.reason && <p>Reason: {batch.reason}</p>}
        <div className="table-wrap">
          <table className="grid">
            <caption className="vh">The campaigns of this bulk send, one per baseline.</caption>
            <thead>
              <tr>
                <th scope="col">Baseline</th>
                <th scope="col" className="num">
                  Vendors
                </th>
                <th scope="col">Answer by</th>
                <th scope="col">Campaign</th>
              </tr>
            </thead>
            <tbody>
              {batch.campaigns.map((c) => (
                <tr key={c.id}>
                  <td>
                    {c.questionnaireName}
                    <span className="cell-sub mono">
                      {c.questionnaireCode} v{c.versionNo}
                      {c.riskModelCode ? ', ' + c.riskModelCode : ''}
                    </span>
                  </td>
                  <td className="num mono">{c.recipients}</td>
                  <td>{c.dueOn ? shortDate(c.dueOn) : <span className="muted">no date</span>}</td>
                  <td>
                    <a href={'#/campaign/' + encodeURIComponent(c.id)}>{c.status === 'DRAFT' ? 'Waiting' : c.status.charAt(0) + c.status.slice(1).toLowerCase().replace(/_/g, ' ')}</a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {batch.overrides.length > 0 && (
          <>
            <h2>Changed rows</h2>
            <ul className="bl-changes">
              {batch.overrides.map((o) => (
                <li key={o.vendorId}>
                  <strong>{o.vendorName}</strong>: {o.proposedQuestionnaire ? o.proposedQuestionnaire + ' (' + o.proposedWhy + ')' : 'nothing proposed'} to {o.chosenQuestionnaire}.{' '}
                  <span className="muted">
                    {o.reason}, by {o.by}
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
        {approved && (
          <div className="note" role="status">
            <strong>
              {approved.campaigns.filter((c) => c.outcome === 'APPROVED').length} of {approved.campaigns.length} approved
            </strong>
            {approved.counts.issued ?? 0} assessments issued, {approved.counts.deferred ?? 0} held for an open one, {approved.counts.suppressed ?? 0} left out.
            <ul>
              {approved.campaigns
                .filter((c) => c.outcome !== 'APPROVED')
                .map((c) => (
                  <li key={c.id}>
                    {c.name}: {c.message}
                  </li>
                ))}
            </ul>
          </div>
        )}
        {approveError && <ErrorBox error={approveError} what="The bulk send" verb="approved" />}
        {batch.status === 'DRAFT' && waiting > 0 && (
          <div className="bl-foot-bar">
            <span className="muted">{approval.who}</span>
            {approval.youMay ? (
              <button type="button" className="primary" disabled={approving} onClick={approve}>
                {approving ? 'Approving' : 'Approve ' + waiting + ' campaign' + (waiting === 1 ? '' : 's')}
              </button>
            ) : (
              <span className="sw-row">
                <Status tone="neutral">Not yours to approve</Status>
                <ReasonTip label="Why you cannot approve it" text={approval.why} />
              </span>
            )}
          </div>
        )}
      </section>
    </>
  )
}
